import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  People,
  CheckCircle,
  Timeline,
  Assignment,
  AttachMoney,
  Favorite,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const visaTypes = [
  {
    title: 'Spouse/Partner Visa',
    description: 'For those married to or in a civil partnership with a UK citizen/resident',
    requirements: [
      'Valid relationship proof',
      'Financial requirement (£18,600 minimum)',
      'English language requirement',
      'Adequate accommodation',
    ],
  },
  {
    title: 'Child Dependent Visa',
    description: 'For children under 18 joining parents in the UK',
    requirements: [
      'Parent settled/settling in the UK',
      'Proof of relationship',
      'Adequate care arrangements',
      'No other parent with custody abroad',
    ],
  },
  {
    title: 'Parent Visa',
    description: 'For parents of British children or settled persons',
    requirements: [
      'Child under 18 in the UK',
      'Sole responsibility/access rights',
      'No alternative care arrangement',
      'Financial requirement',
    ],
  },
  {
    title: 'Adult Dependent Relative',
    description: 'For adult relatives requiring long-term care',
    requirements: [
      'Requiring long-term care',
      'No adequate care in home country',
      'Financial support evidence',
      'Close family relationship',
    ],
  },
];

const benefits = [
  'Live together with family in the UK',
  'Access to NHS healthcare',
  'Right to work in the UK',
  'Path to permanent settlement',
  'Access to education',
  'Travel flexibility',
];

const process = [
  {
    title: 'Initial Assessment',
    description: 'Evaluate your eligibility and visa category',
  },
  {
    title: 'Document Collection',
    description: 'Guidance on required documentation and evidence',
  },
  {
    title: 'Financial Assessment',
    description: 'Review and prepare financial requirements',
  },
  {
    title: 'Application Filing',
    description: 'Professional support with visa application',
  },
  {
    title: 'Settlement Support',
    description: 'Guidance on settling in the UK with your family',
  },
];

const FamilyVisa = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <People sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Family Visa Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Reunite with your loved ones in the United Kingdom
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Family Life in the UK
            </Typography>
            <Typography variant="body1" paragraph>
              The UK family visa routes allow families to live together in the United Kingdom. Whether you're joining a spouse, partner, child, or parent, our expert team provides comprehensive support throughout your visa application process.
            </Typography>
            <Typography variant="body1">
              We understand the importance of family unity and will guide you through every step to ensure a successful application and smooth transition to life in the UK.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Key Benefits
                </Typography>
                <List>
                  {benefits.map((benefit) => (
                    <ListItem key={benefit}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Visa Types Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Types of Family Visas
          </Typography>
          <Grid container spacing={4}>
            {visaTypes.map((visa) => (
              <Grid item xs={12} md={6} key={visa.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {visa.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {visa.description}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Key Requirements:
                    </Typography>
                    <List>
                      {visa.requirements.map((req) => (
                        <ListItem key={req}>
                          <ListItemIcon>
                            <Assignment color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={req} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Application Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Guidance
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers specializes in UK family visas. We stay updated with the latest immigration rules and policies to provide you with accurate advice and support.
                  </Typography>
                  <Typography variant="body1">
                    We have helped numerous families successfully reunite in the United Kingdom through various family visa routes.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Favorite color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Compassionate Support
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We understand that family separation can be challenging. Our team provides compassionate support and clear communication throughout your visa application process.
                  </Typography>
                  <Typography variant="body1">
                    We offer comprehensive assistance with document preparation, financial requirement assessment, and settlement guidance to ensure your family's smooth transition to the UK.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Ready to Reunite with Your Family?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FamilyVisa; 